<template>
  <div class="calc-fire">
    <!-- Header -->
    <div class="d-flex align-items-center mb-3">
      <div class="calc-icon me-2"><i class="bi bi-fire"></i></div>
      <div>
        <h5 class="mb-0 fw-bold" style="color:#212529;">FIRE</h5>
        <small class="text-muted" style="font-size:0.78rem;">
          Euros nominales · Aportaciones crecen con IPC · Fiscal solo sobre plusvalía
        </small>
      </div>
    </div>

    <!-- KPIs -->
    <div class="row g-3 mb-3">
      <div class="col-6 col-md-3">
        <div class="card shadow-sm h-100" style="border-left:4px solid #0dcaf0; background:#fff;">
          <div class="card-body py-3">
            <div class="kpi-label"><i class="bi bi-calendar-event me-1"></i>Mes/Año FIRE</div>
            <div class="kpi-value" v-if="resultado.alcanzado">{{ resultado.mesAnoFire }}</div>
            <div class="kpi-value text-muted" v-else>—</div>
            <div class="kpi-sub" v-if="resultado.alcanzado">
              {{ resultado.edadFire }} años · faltan {{ resultado.faltanTexto }}
            </div>
            <div class="kpi-sub text-warning" v-else>No alcanzado en {{ params.horizonte }} años</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card shadow-sm h-100" style="border-left:4px solid #6f42c1; background:#fff;">
          <div class="card-body py-3">
            <div class="kpi-label"><i class="bi bi-piggy-bank me-1"></i>Patrimonio FIRE</div>
            <div class="kpi-value" style="color:#6f42c1;">{{ formatCompact(resultado.patrimonioFire) }}</div>
            <div class="kpi-sub" v-if="resultado.alcanzado">nominal a {{ resultado.mesAnoFire.toLowerCase() }}</div>
            <div class="kpi-sub text-muted" v-else>final del horizonte</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card shadow-sm h-100" style="border-left:4px solid #6f42c1; background:#fff;">
          <div class="card-body py-3">
            <div class="kpi-label"><i class="bi bi-cash-coin me-1"></i>Renta nominal</div>
            <div class="kpi-value" style="color:#6f42c1;">
              {{ formatEntero(resultado.rentaNominalMes) }} <small style="font-size:0.75rem; color:#6c757d; font-weight:500;">€/mes</small>
            </div>
            <div class="kpi-sub">gasto obj. {{ formatEntero(resultado.gastoNominalMes) }} €</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card shadow-sm h-100" style="border-left:4px solid #198754; background:#fff;">
          <div class="card-body py-3">
            <div class="kpi-label"><i class="bi bi-house-heart me-1"></i>Renta real hoy</div>
            <div class="kpi-value text-success">
              {{ formatEntero(resultado.rentaRealHoyMes) }} <small style="font-size:0.75rem; color:#6c757d; font-weight:500;">€/mes</small>
            </div>
            <div class="kpi-sub" :class="resultado.diffReal >= 0 ? 'text-success' : 'text-danger'">
              {{ resultado.diffReal >= 0 ? '+' : '' }}{{ formatEntero(resultado.diffReal) }} sobre obj.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sub-tabs -->
    <ul class="nav nav-pills calc-pills mb-3">
      <li class="nav-item flex-fill">
        <button class="nav-link w-100" :class="{ active: subtab === 'tabla' }" @click="subtab = 'tabla'">
          <i class="bi bi-table me-1"></i>Tabla
        </button>
      </li>
      <li class="nav-item flex-fill">
        <button class="nav-link w-100" :class="{ active: subtab === 'parametros' }" @click="subtab = 'parametros'">
          <i class="bi bi-sliders me-1"></i>Parámetros
        </button>
      </li>
      <li class="nav-item flex-fill">
        <button class="nav-link w-100" :class="{ active: subtab === 'grafico' }" @click="subtab = 'grafico'">
          <i class="bi bi-graph-up me-1"></i>Gráfico
        </button>
      </li>
      <li class="nav-item flex-fill">
        <button class="nav-link w-100" :class="{ active: subtab === 'meta' }" @click="subtab = 'meta'">
          <i class="bi bi-bullseye me-1"></i>Meta
        </button>
      </li>
    </ul>

    <!-- Parámetros -->
    <div v-if="subtab === 'parametros'" class="card shadow-sm">
      <div class="card-body">
        <div class="lock-banner mb-3">
          <div class="lock-banner-text">
            <i class="bi bi-info-circle me-1"></i>
            Los sliders están bloqueados para evitar cambios accidentales. Pulsa
            <i class="bi bi-lock-fill mx-1"></i> en cada parámetro para editarlo.
          </div>
          <button
            type="button"
            class="btn btn-sm lock-banner-btn"
            :class="algunDesbloqueado ? 'btn-outline-secondary' : 'btn-outline-primary'"
            @click="algunDesbloqueado ? bloquearTodos() : desbloquearTodos()"
          >
            <i class="bi me-1" :class="algunDesbloqueado ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
            {{ algunDesbloqueado ? 'Bloquear todos' : 'Desbloquear todos' }}
          </button>
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-person-vcard"></i>
              <span>Año de nacimiento · {{ edadActual }} años</span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ params.anoNacimiento }}</span>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.anoNacimiento }"
                :title="slidersBloqueados.anoNacimiento ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.anoNacimiento ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('anoNacimiento')"
              >
                <i class="bi" :class="slidersBloqueados.anoNacimiento ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="1940" max="2010" step="1"
            :disabled="slidersBloqueados.anoNacimiento"
            v-model.number="params.anoNacimiento"
          >
        </div>

        <div class="param-group">
          <div class="param-group-title">Cartera inicial</div>

          <div class="param-block">
            <div class="param-row">
              <div class="param-label">
                <i class="bi bi-wallet2"></i>
                <span>Patrimonio actual (euros)</span>
                <span
                  v-if="patrimonioSugeridoLabel"
                  class="badge bg-info-subtle text-info ms-1"
                  :title="`Sugerido por ${patrimonioSugeridoLabel}: ${formatEuros(patrimonioSugerido)}`"
                  style="font-size:0.65rem; font-weight:600;"
                >
                  <i class="bi bi-link-45deg"></i> {{ patrimonioSugeridoLabel }}
                </span>
              </div>
              <div class="param-value d-flex align-items-center gap-1">
                <span>{{ formatEuros(params.patrimonioActual) }}</span>
                <button
                  v-if="patrimonioSugeridoLabel && Math.round(params.patrimonioActual) !== Math.round(patrimonioSugerido)"
                  class="btn btn-sm p-0 ms-1 btn-reset-aporte"
                  @click="restaurarPatrimonio"
                  :title="`Restaurar valor de ${patrimonioSugeridoLabel}: ${formatEuros(patrimonioSugerido)}`"
                >
                  <i class="bi bi-arrow-counterclockwise"></i>
                </button>
                <button
                  type="button"
                  class="btn-lock"
                  :class="{ 'is-open': !slidersBloqueados.patrimonioActual }"
                  :title="slidersBloqueados.patrimonioActual ? 'Desbloquear para editar' : 'Bloquear'"
                  :aria-label="slidersBloqueados.patrimonioActual ? 'Desbloquear' : 'Bloquear'"
                  @click="toggleBloqueo('patrimonioActual')"
                >
                  <i class="bi" :class="slidersBloqueados.patrimonioActual ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
                </button>
              </div>
            </div>
            <input
              type="range" class="fire-slider"
              min="0" :max="maxPatrimonioSlider" step="1000"
              :disabled="slidersBloqueados.patrimonioActual"
              v-model.number="params.patrimonioActual"
            >
          </div>

          <div class="param-block">
            <div class="param-row">
              <div class="param-label">
                <i class="bi bi-graph-up-arrow"></i>
                <span>Beneficio latente (%)</span>
              </div>
              <div class="param-value d-flex align-items-center gap-1">
                <span>
                  {{ params.beneficioLatente.toFixed(0) }}%
                  <small class="text-muted fw-normal">→ plusvalía {{ formatEuros(plusvaliaActual) }}</small>
                </span>
                <button
                  type="button"
                  class="btn-lock"
                  :class="{ 'is-open': !slidersBloqueados.beneficioLatente }"
                  :title="slidersBloqueados.beneficioLatente ? 'Desbloquear para editar' : 'Bloquear'"
                  :aria-label="slidersBloqueados.beneficioLatente ? 'Desbloquear' : 'Bloquear'"
                  @click="toggleBloqueo('beneficioLatente')"
                >
                  <i class="bi" :class="slidersBloqueados.beneficioLatente ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
                </button>
              </div>
            </div>
            <input
              type="range" class="fire-slider"
              min="0" max="100" step="1"
              :disabled="slidersBloqueados.beneficioLatente"
              v-model.number="params.beneficioLatente"
            >
          </div>

          <div class="row g-2 mt-2">
            <div class="col-6">
              <div class="mini-card">
                <div class="mini-label">Base de coste</div>
                <div class="mini-value">{{ formatEuros(baseCosteActual) }}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="mini-card">
                <div class="mini-label">Plusvalía hoy</div>
                <div class="mini-value">{{ formatEuros(plusvaliaActual) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-arrow-up-circle"></i>
              <span>Aportación base/mes (euros de hoy)</span>
              <span
                v-if="aportacionSugeridoLabel"
                class="badge bg-info-subtle text-info ms-1"
                :title="`Sugerido por ${aportacionSugeridoLabel}: ${formatEntero(aportacionSugeridaMes)} €/mes`"
                style="font-size:0.65rem; font-weight:600;"
              >
                <i class="bi bi-link-45deg"></i> {{ aportacionSugeridoLabel }}
              </span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ formatEntero(params.aportacionMes) }} €</span>
              <button
                v-if="aportacionSugeridoLabel && Math.round(params.aportacionMes) !== Math.round(aportacionSugeridaMes)"
                class="btn btn-sm p-0 ms-1 btn-reset-aporte"
                @click="restaurarAportacionBase"
                :title="`Restaurar valor de ${aportacionSugeridoLabel}: ${formatEntero(aportacionSugeridaMes)} €/mes`"
              >
                <i class="bi bi-arrow-counterclockwise"></i>
              </button>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.aportacionMes }"
                :title="slidersBloqueados.aportacionMes ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.aportacionMes ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('aportacionMes')"
              >
                <i class="bi" :class="slidersBloqueados.aportacionMes ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="0" :max="maxAportacionSlider" step="50"
            :disabled="slidersBloqueados.aportacionMes"
            v-model.number="params.aportacionMes"
          >
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-bar-chart-line"></i>
              <span>Rentabilidad nominal (%)</span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ params.rentabilidad.toFixed(1) }}%</span>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.rentabilidad }"
                :title="slidersBloqueados.rentabilidad ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.rentabilidad ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('rentabilidad')"
              >
                <i class="bi" :class="slidersBloqueados.rentabilidad ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="0" max="15" step="0.1"
            :disabled="slidersBloqueados.rentabilidad"
            v-model.number="params.rentabilidad"
          >
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-thermometer-half"></i>
              <span>Inflación (%) · sube aportaciones y gasto</span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ params.inflacion.toFixed(1) }}%</span>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.inflacion }"
                :title="slidersBloqueados.inflacion ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.inflacion ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('inflacion')"
              >
                <i class="bi" :class="slidersBloqueados.inflacion ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="0" max="8" step="0.1"
            :disabled="slidersBloqueados.inflacion"
            v-model.number="params.inflacion"
          >
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-cart"></i>
              <span>Gasto mensual objetivo (euros de hoy)</span>
              <span
                v-if="gastoMesSugeridoLabel"
                class="badge bg-info-subtle text-info ms-1"
                :title="`Sugerido por ${gastoMesSugeridoLabel}: ${formatEntero(gastoMesSugerido)} €/mes`"
                style="font-size:0.65rem; font-weight:600;"
              >
                <i class="bi bi-link-45deg"></i> {{ gastoMesSugeridoLabel }}
              </span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ formatEntero(params.gastoMes) }} €</span>
              <button
                v-if="gastoMesSugeridoLabel && Math.round(params.gastoMes) !== Math.round(gastoMesSugerido)"
                class="btn btn-sm p-0 ms-1 btn-reset-aporte"
                @click="restaurarGastoMes"
                :title="`Restaurar valor de ${gastoMesSugeridoLabel}: ${formatEntero(gastoMesSugerido)} €/mes`"
              >
                <i class="bi bi-arrow-counterclockwise"></i>
              </button>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.gastoMes }"
                :title="slidersBloqueados.gastoMes ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.gastoMes ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('gastoMes')"
              >
                <i class="bi" :class="slidersBloqueados.gastoMes ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="500" :max="maxGastoSlider" step="50"
            :disabled="slidersBloqueados.gastoMes"
            v-model.number="params.gastoMes"
          >
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-percent"></i>
              <span>Tasa retiro FIRE (%)</span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ params.tasaRetiro.toFixed(1) }}%</span>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.tasaRetiro }"
                :title="slidersBloqueados.tasaRetiro ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.tasaRetiro ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('tasaRetiro')"
              >
                <i class="bi" :class="slidersBloqueados.tasaRetiro ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="2" max="6" step="0.1"
            :disabled="slidersBloqueados.tasaRetiro"
            v-model.number="params.tasaRetiro"
          >
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-cash-stack"></i>
              <span>Retiro mensual tras FIRE (€ de hoy)</span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ formatEntero(params.retiroMensualPostFire) }} €</span>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.retiroMensualPostFire }"
                :title="slidersBloqueados.retiroMensualPostFire ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.retiroMensualPostFire ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('retiroMensualPostFire')"
              >
                <i class="bi" :class="slidersBloqueados.retiroMensualPostFire ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="0" :max="maxRetiroMensualSlider" step="50"
            :disabled="slidersBloqueados.retiroMensualPostFire"
            v-model.number="params.retiroMensualPostFire"
          >
          <div class="form-text small text-muted mt-1">
            Mismo criterio que el gasto objetivo: euros de hoy; en la simulación crece con IPC. Reduce el patrimonio mes a mes tras el FIRE (en la tabla puedes fijar otro importe por año).
          </div>
        </div>

        <div class="param-block">
          <div class="param-row">
            <div class="param-label">
              <i class="bi bi-bank"></i>
              <span>Retención fiscal sobre plusvalía (%)</span>
            </div>
            <div class="param-value d-flex align-items-center gap-1">
              <span>{{ params.retencionFiscal.toFixed(1) }}%</span>
              <button
                type="button"
                class="btn-lock"
                :class="{ 'is-open': !slidersBloqueados.retencionFiscal }"
                :title="slidersBloqueados.retencionFiscal ? 'Desbloquear para editar' : 'Bloquear'"
                :aria-label="slidersBloqueados.retencionFiscal ? 'Desbloquear' : 'Bloquear'"
                @click="toggleBloqueo('retencionFiscal')"
              >
                <i class="bi" :class="slidersBloqueados.retencionFiscal ? 'bi-lock-fill' : 'bi-unlock-fill'"></i>
              </button>
            </div>
          </div>
          <input
            type="range" class="fire-slider"
            min="0" max="50" step="0.5"
            :disabled="slidersBloqueados.retencionFiscal"
            v-model.number="params.retencionFiscal"
          >
        </div>

        <div class="modelo-card mt-3">
          <div class="modelo-title">Modelo de cálculo</div>
          <div class="modelo-row">
            <span>Patrimonio</span>
            <span class="text-primary fw-semibold">euros nominales</span>
          </div>
          <div class="modelo-row">
            <span>Rentabilidad</span>
            <span class="text-primary fw-semibold">{{ params.rentabilidad.toFixed(1) }}% nominal bruto</span>
          </div>
          <div class="modelo-row">
            <span>Granularidad</span>
            <span class="text-primary fw-semibold">mensual (mes/año exacto del FIRE)</span>
          </div>
        </div>

        <div class="text-center mt-3">
          <button class="btn btn-sm btn-outline-secondary" @click="resetParams">
            <i class="bi bi-arrow-counterclockwise me-1"></i>Restaurar valores por defecto
          </button>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div v-if="subtab === 'tabla'" class="card shadow-sm">
      <div class="card-body p-0">
        <div class="px-3 py-2 small text-muted border-bottom" style="background:#f8f9fa;">
          <i class="bi bi-info-circle me-1"></i>
          Patrimonio y rentas mostrados a <strong>final de cada año natural</strong> (31 de diciembre).
          La primera fila es la foto de hoy.
          El mes en curso no se incluye en APORT. ANUAL del año parcial: esa cuota ya está en el patrimonio de hoy.
          Tras el <strong>FIRE</strong>, la columna <strong>Retiro mensual</strong> descuenta del patrimonio (valor por defecto desde el perfil, escalado con inflación como el gasto; editable por año).
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 calc-table">
            <thead class="table-light">
              <tr>
                <th>Año</th>
                <th class="text-end">Edad</th>
                <th class="text-end" :title="tooltipAporteAnual">
                  Aport. anual
                  <i class="bi bi-pencil-square ms-1 text-primary" :title="tooltipAporteAnual" style="cursor:help; font-size:0.72rem;"></i>
                </th>
                <th class="text-end">Aport. mes</th>
                <th class="text-end" :title="tooltipRetiroMensual">
                  Retiro mensual
                  <i class="bi bi-pencil-square ms-1 text-primary" :title="tooltipRetiroMensual" style="cursor:help; font-size:0.72rem;"></i>
                </th>
                <th class="text-end" :title="tooltipPatrimonio">
                  Patrimonio
                  <i class="bi bi-info-circle-fill ms-1 text-primary" :title="tooltipPatrimonio" style="cursor:help; font-size:0.72rem;"></i>
                </th>
                <th class="text-end ganancia-th">
                  <div class="d-flex flex-column align-items-end gap-1">
                    <span :title="tooltipGananciaCol">
                      {{ mostrarGananciaMes ? 'Ganancia mes' : 'Ganancia año' }}
                      <i class="bi bi-info-circle-fill ms-1 text-primary" style="cursor:help; font-size:0.72rem;"></i>
                    </span>
                    <div class="form-check form-switch form-check-reverse m-0 ganancia-switch">
                      <input
                        id="toggle-ganancia-mes"
                        v-model="mostrarGananciaMes"
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        aria-label="Mostrar ganancia como media mensual"
                      >
                      <label class="form-check-label small text-muted mb-0" for="toggle-ganancia-mes">Mes</label>
                    </div>
                  </div>
                </th>
                <th class="text-end">Plusvalía</th>
                <th class="text-end">Renta neta/mes</th>
                <th class="text-end">Gasto obj./mes</th>
                <th class="text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="fila in resultado.filas"
                :key="fila.label"
                :class="[fila.esFire ? 'fila-fire' : '', fila.esHoy ? 'fila-hoy' : '', fila.esRetiro ? 'fila-retiro' : '']"
              >
                <td class="fw-semibold col-ano">
                  <span v-if="fila.esHoy">Hoy</span>
                  <span v-else>{{ fila.ano }}</span>
                  <span v-if="fila.parcial" class="badge bg-info-subtle text-info ms-1" style="font-size:0.65rem; font-weight:600;">
                    {{ fila.mesesParciales }}m
                  </span>
                  <span v-if="fila.esHoy" class="d-block text-muted" style="font-size:0.72rem; font-weight:400;">
                    {{ fila.fechaTexto }}
                  </span>
                  <span v-if="fila.esFire" class="d-block text-success" style="font-size:0.72rem; font-weight:600;">
                    <i class="bi bi-fire"></i> FIRE en {{ fila.mesFireTexto }}
                  </span>
                </td>
                <td class="text-end">{{ fila.edad }}</td>
                <td class="text-end">
                  <span v-if="fila.esHoy" class="text-muted">—</span>
                  <div v-else class="d-flex align-items-center justify-content-end gap-1">
                    <input
                      type="number" min="0" step="100"
                      class="form-control form-control-sm aporte-input"
                      :class="{ 'aporte-manual': fila.aporteEsManual }"
                      :value="Math.round(fila.aportacionAnual)"
                      @change="onAporteAnualChange(fila, $event)"
                      :title="`Aportación anual ${fila.ano}${fila.parcial ? ` (${fila.mesesParciales} meses)` : ''}`"
                    />
                    <button
                      v-if="fila.aporteEsManual"
                      class="btn btn-sm p-0 ms-1 btn-reset-aporte"
                      @click="restaurarAporte(fila.ano)"
                      title="Restaurar valor por defecto"
                    >
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                  </div>
                </td>
                <td class="text-end col-mes">
                  <span v-if="fila.esHoy" class="text-muted">—</span>
                  <span v-else>{{ formatEuros(fila.aporteMes) }}</span>
                </td>
                <td class="text-end">
                  <span v-if="fila.esHoy" class="text-muted">—</span>
                  <div v-else class="d-flex align-items-center justify-content-end gap-1">
                    <input
                      type="number"
                      min="0"
                      step="100"
                      class="form-control form-control-sm aporte-input"
                      :class="{ 'aporte-manual': fila.retiroEsManual }"
                      :disabled="resultado.anoFire == null || fila.ano < resultado.anoFire"
                      :value="Math.round(fila.retiroMensual)"
                      :title="`Retiro mensual nominal ${fila.ano} (desde el mes FIRE).`"
                      @change="onRetiroMensualChange(fila, $event)"
                    />
                    <button
                      v-if="fila.retiroEsManual"
                      class="btn btn-sm p-0 ms-1 btn-reset-aporte"
                      title="Restaurar retiro por defecto"
                      type="button"
                      @click="restaurarRetiro(fila.ano)"
                    >
                      <i class="bi bi-arrow-counterclockwise"></i>
                    </button>
                  </div>
                </td>
                <td class="text-end fw-semibold col-patrimonio" :title="fila.esHoy ? 'Patrimonio actual' : `Patrimonio al 31 dic ${fila.ano}`">
                  {{ formatEuros(fila.patrimonio) }}
                </td>
                <td
                  class="text-end col-ganancia-ano"
                  :class="fila.esHoy ? '' : (gananciaMostrada(fila) >= 0 ? 'text-success fw-semibold' : 'text-danger fw-semibold')"
                  :title="fila.esHoy
                    ? 'Punto de partida.'
                    : (mostrarGananciaMes
                      ? 'Ganancia neta media mensual en el período de la fila.'
                      : 'Ganancia neta del año (variación − aportaciones + retiros).')"
                >
                  <span v-if="fila.esHoy" class="text-muted">—</span>
                  <span v-else>{{ formatEuros(gananciaMostrada(fila)) }}</span>
                </td>
                <td class="text-end col-secundaria">{{ formatEuros(fila.plusvalia) }}</td>
                <td class="text-end" :class="fila.fire ? 'text-success fw-semibold' : ''">{{ formatEuros(fila.rentaNetaMes) }}</td>
                <td class="text-end col-secundaria">{{ formatEuros(fila.gastoMes) }}</td>
                <td class="text-center">
                  <span v-if="fila.esHoy" class="badge bg-secondary-subtle text-secondary">Inicio</span>
                  <span v-else-if="fila.esFire" class="badge bg-success">FIRE</span>
                  <span v-else-if="fila.esRetiro" class="badge bg-purple-subtle text-purple"><i class="bi bi-cup-hot me-1"></i>Retiro</span>
                  <span v-else-if="fila.fire" class="badge bg-success-subtle text-success">OK</span>
                  <span v-else class="badge bg-light text-muted border">Acum.</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Gráfico -->
    <div v-if="subtab === 'grafico'" class="card shadow-sm">
      <div class="card-body">
        <div style="height: 380px; position: relative;">
          <Line :data="chartData" :options="chartOptions" />
        </div>
        <div class="legend-text mt-2">
          <span class="dot" style="background:#6f42c1"></span> Patrimonio nominal
          <span class="dot ms-3" style="background:#198754"></span> Renta neta mensual
          <span class="dot ms-3" style="background:#fd7e14"></span> Gasto objetivo mensual
          <span class="dot ms-3" style="background:#d63384"></span> Retiro mensual (post-FIRE)
        </div>
      </div>
    </div>

    <!-- Meta patrimonio -->
    <div v-if="subtab === 'meta'" class="card shadow-sm">
      <div class="card-body">
        <p class="small text-muted mb-3">
          <i class="bi bi-info-circle me-1"></i>
          Indica un patrimonio nominal objetivo. La fecha se calcula con los mismos parámetros
          y aportaciones/retiros de la simulación (primer mes en que el patrimonio lo alcanza o supera).
        </p>
        <div class="meta-input-row">
          <label class="meta-label" for="meta-patrimonio">Patrimonio objetivo</label>
          <div class="meta-input-wrap">
            <input
              id="meta-patrimonio"
              v-model.number="metaObjetivo"
              type="number"
              min="0"
              step="1000"
              class="form-control form-control-lg meta-input"
              placeholder="Ej. 1 000 000"
            >
            <span class="meta-suffix">€</span>
          </div>
        </div>
        <div v-if="metaObjetivo > 0" class="meta-result mt-4">
          <template v-if="metaAlcance.alcanzado">
            <div class="meta-result-icon text-success">
              <i class="bi bi-calendar-check"></i>
            </div>
            <div>
              <div class="meta-result-title">Fecha estimada</div>
              <div class="meta-result-date">{{ metaAlcance.mesAnoTexto }}</div>
              <div class="meta-result-sub text-muted">
                {{ metaAlcance.fechaTexto }} · {{ metaAlcance.edad }} años
                <span v-if="metaAlcance.yaAlcanzado" class="badge bg-success-subtle text-success ms-2">Ya alcanzado</span>
                <span v-else>· faltan {{ metaAlcance.faltanTexto }}</span>
              </div>
              <div class="meta-result-sub small text-muted mt-1">
                Patrimonio en ese mes: {{ formatEuros(metaAlcance.patrimonio) }}
              </div>
            </div>
          </template>
          <template v-else>
            <div class="meta-result-icon text-warning">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div>
              <div class="meta-result-title">No alcanzado</div>
              <p class="mb-0 text-muted small">
                Con los parámetros actuales no se llega a {{ formatEuros(metaObjetivo) }}
                en los próximos {{ params.horizonte }} años.
                <span v-if="resultado.patrimonioFire > 0">
                  Patrimonio al final del horizonte: {{ formatCompact(resultado.patrimonioFire) }}.
                </span>
              </p>
            </div>
          </template>
        </div>
        <p v-else class="text-muted small mb-0 mt-3">Introduce una cifra para ver la fecha estimada.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { Line } from 'vue-chartjs';
import { normalizeFirePrefs } from '../utils/firePrefs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const ANO_NACIMIENTO_DEFAULT = 1987;
const HORIZONTE_DEFAULT = 50;
const MESES_ABREV = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

const valoresDefault = (patrimonioInicial) => ({
  anoNacimiento: ANO_NACIMIENTO_DEFAULT,
  patrimonioActual: patrimonioInicial,
  beneficioLatente: 30,
  aportacionMes: 2200,
  /** Retiro mensual nominal (€ de hoy) tras el FIRE; 0 = sin retiro por defecto. */
  retiroMensualPostFire: 0,
  rentabilidad: 7.0,
  inflacion: 2.0,
  gastoMes: 2300,
  tasaRetiro: 3.5,
  retencionFiscal: 20.0,
  horizonte: HORIZONTE_DEFAULT
});

const formatMesAno = (date) => `${MESES_ABREV[date.getMonth()]} ${date.getFullYear()}`;

/** Meses de aportación que quedan en el año. El mes en curso ya está en el patrimonio. */
const mesesRestantesPrimerAno = (mesIndex) => Math.max(0, 11 - mesIndex);

const formatFaltanMeses = (mesGlobal) => {
  const yearsExact = mesGlobal / 12;
  const yearsInt = Math.floor(yearsExact);
  const monthsRem = Math.round((yearsExact - yearsInt) * 12);
  if (yearsInt === 0) {
    return `${monthsRem} mes${monthsRem === 1 ? '' : 'es'}`;
  }
  if (monthsRem === 0) {
    return `${yearsInt} año${yearsInt === 1 ? '' : 's'}`;
  }
  return `${yearsInt}a ${monthsRem}m`;
};

export default {
  name: 'CalculadoraFire',
  components: { Line },
  props: {
    patrimonioInicial: {
      type: Number,
      default: 433000
    },
    /** Si true, el patrimonio se rehidrata desde patrimonioInicial al cambiar (sigue editable como simulación). */
    externoPatrimonio: {
      type: Boolean,
      default: false
    },
    /** Etiqueta corta de origen para el patrimonio sugerido (p. ej. "Evolución"). */
    patrimonioSugeridoLabel: {
      type: String,
      default: ''
    },
    /** Aportación mensual sugerida (€/mes hoy). Si > 0 se usa como valor inicial. (Reservado, actualmente sin origen externo.) */
    aportacionSugeridaMes: {
      type: Number,
      default: 0
    },
    /** Etiqueta corta de origen para la aportación sugerida. */
    aportacionSugeridoLabel: {
      type: String,
      default: ''
    },
    /** Gasto mensual objetivo sugerido (€/mes hoy). Si > 0 se usa como valor inicial. */
    gastoMesSugerido: {
      type: Number,
      default: 0
    },
    /** Etiqueta corta de origen para el gasto sugerido (p. ej. "Último año natural"). */
    gastoMesSugeridoLabel: {
      type: String,
      default: ''
    },
    /** Preferencias guardadas en perfil (normalizadas). */
    savedPrefs: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const subtab = ref('tabla');
    const metaObjetivo = ref(0);
    const params = reactive(valoresDefault(props.patrimonioInicial > 0 ? props.patrimonioInicial : 433000));

    const rehydrateFromProps = () => {
      const pi = props.patrimonioInicial > 0 ? props.patrimonioInicial : 433000;
      Object.assign(params, valoresDefault(pi));
      if (props.savedPrefs && typeof props.savedPrefs === 'object') {
        const n = normalizeFirePrefs(props.savedPrefs);
        params.anoNacimiento = n.anoNacimiento;
        params.beneficioLatente = n.beneficioLatente;
        params.aportacionMes = n.aportacionMes;
        params.retiroMensualPostFire = n.retiroMensualPostFire;
        params.rentabilidad = n.rentabilidad;
        params.inflacion = n.inflacion;
        params.gastoMes = n.gastoMes;
        params.tasaRetiro = n.tasaRetiro;
        params.retencionFiscal = n.retencionFiscal;
        params.horizonte = n.horizonte;
        if (!props.externoPatrimonio) {
          params.patrimonioActual = n.patrimonioActual;
        }
      }
      if (props.externoPatrimonio && props.patrimonioInicial > 0) {
        params.patrimonioActual = props.patrimonioInicial;
      }
      // La aportación sugerida (admin) tiene prioridad si se proporciona
      if (props.aportacionSugeridaMes > 0) {
        params.aportacionMes = Math.round(props.aportacionSugeridaMes);
      }
      // El gasto mensual sugerido (admin, último año natural) tiene prioridad si se proporciona
      if (props.gastoMesSugerido > 0) {
        params.gastoMes = Math.round(props.gastoMesSugerido);
      }
    };

    const restaurarPatrimonio = () => {
      if (props.patrimonioInicial > 0) {
        params.patrimonioActual = props.patrimonioInicial;
      }
    };

    const restaurarAportacionBase = () => {
      if (props.aportacionSugeridaMes > 0) {
        params.aportacionMes = Math.round(props.aportacionSugeridaMes);
      }
    };

    const restaurarGastoMes = () => {
      if (props.gastoMesSugerido > 0) {
        params.gastoMes = Math.round(props.gastoMesSugerido);
      }
    };

    const maxPatrimonioSlider = computed(() => {
      const base = 3_000_000;
      const valor = Number(params.patrimonioActual) || 0;
      if (valor <= base) return base;
      // Redondea hacia arriba al siguiente medio millón para mantener un slider amplio
      return Math.ceil(valor / 500_000) * 500_000;
    });

    const maxAportacionSlider = computed(() => {
      const base = 6_000;
      const valor = Number(params.aportacionMes) || 0;
      if (valor <= base) return base;
      return Math.ceil(valor / 500) * 500;
    });

    const maxGastoSlider = computed(() => {
      const base = 8_000;
      const valor = Number(params.gastoMes) || 0;
      if (valor <= base) return base;
      return Math.ceil(valor / 500) * 500;
    });

    const maxRetiroMensualSlider = computed(() => {
      const base = 8_000;
      const valor = Number(params.retiroMensualPostFire) || 0;
      if (valor <= base) return base;
      return Math.ceil(valor / 500) * 500;
    });

    watch(
      () => [props.patrimonioInicial, props.externoPatrimonio, props.savedPrefs, props.aportacionSugeridaMes, props.gastoMesSugerido],
      () => {
        rehydrateFromProps();
      },
      { deep: true, immediate: true }
    );

    // Mapa { año: aporteMensualNominal } con aportaciones manuales por año.
    // Si una key no existe se usa el comportamiento por defecto (aporte base
    // creciente con IPC pre-FIRE, 0 post-FIRE).
    const aportacionesOverride = ref({});
    /** { año: retiro mensual nominal (€) } tras FIRE; años sin entrada usan retiroMensualPostFire escalado con IPC. */
    const retirosMensualOverride = ref({});
    const mostrarGananciaMes = ref(false);

    // Bloqueo de sliders (true = bloqueado). Por defecto todos bloqueados para
    // evitar que el usuario los mueva sin querer, especialmente en móvil.
    const sliderKeys = [
      'anoNacimiento',
      'patrimonioActual',
      'beneficioLatente',
      'aportacionMes',
      'rentabilidad',
      'inflacion',
      'gastoMes',
      'tasaRetiro',
      'retiroMensualPostFire',
      'retencionFiscal'
    ];
    const buildBloqueosIniciales = () =>
      sliderKeys.reduce((acc, k) => { acc[k] = true; return acc; }, {});
    const slidersBloqueados = reactive(buildBloqueosIniciales());

    const toggleBloqueo = (key) => {
      if (!(key in slidersBloqueados)) return;
      slidersBloqueados[key] = !slidersBloqueados[key];
    };
    const desbloquearTodos = () => {
      for (const k of sliderKeys) slidersBloqueados[k] = false;
    };
    const bloquearTodos = () => {
      for (const k of sliderKeys) slidersBloqueados[k] = true;
    };
    const algunDesbloqueado = computed(() =>
      sliderKeys.some((k) => slidersBloqueados[k] === false)
    );

    const resetParams = () => {
      rehydrateFromProps();
      aportacionesOverride.value = {};
      retirosMensualOverride.value = {};
      bloquearTodos();
    };

    const onAporteAnualChange = (fila, event) => {
      const valor = Math.max(0, parseFloat(event.target.value) || 0);
      const meses = fila.mesesParciales > 0 ? fila.mesesParciales : 12;
      const aporteMes = valor / meses;
      aportacionesOverride.value = {
        ...aportacionesOverride.value,
        [fila.ano]: aporteMes
      };
    };

    const restaurarAporte = (ano) => {
      const nuevo = { ...aportacionesOverride.value };
      delete nuevo[ano];
      aportacionesOverride.value = nuevo;
    };

    const onRetiroMensualChange = (fila, event) => {
      const valor = Math.max(0, parseFloat(event.target.value) || 0);
      retirosMensualOverride.value = {
        ...retirosMensualOverride.value,
        [fila.ano]: valor
      };
    };

    const restaurarRetiro = (ano) => {
      const nuevo = { ...retirosMensualOverride.value };
      delete nuevo[ano];
      retirosMensualOverride.value = nuevo;
    };

    const ahora = new Date();
    const baseAno = ahora.getFullYear();
    const baseMes = ahora.getMonth();
    const edadActual = computed(() => baseAno - params.anoNacimiento);

    const baseCosteActual = computed(() =>
      params.patrimonioActual * (1 - params.beneficioLatente / 100)
    );
    const plusvaliaActual = computed(() =>
      params.patrimonioActual - baseCosteActual.value
    );

    const resultado = computed(() => {
      const r = params.rentabilidad / 100;
      const inf = params.inflacion / 100;
      const tasa = params.tasaRetiro / 100;
      const ret = params.retencionFiscal / 100;
      const monthlyR = Math.pow(1 + r, 1 / 12) - 1;

      let patrimonio = params.patrimonioActual;
      let baseCoste = patrimonio * (1 - params.beneficioLatente / 100);

      const calcRenta = (P, base) => {
        const plus = Math.max(0, P - base);
        const fracPlus = P > 0 ? plus / P : 0;
        const bruta = (P * tasa) / 12;
        const neta = bruta * (1 - ret * fracPlus);
        return { bruta, neta, plus };
      };

      const filas = [];
      const renta0 = calcRenta(patrimonio, baseCoste);
      const hoyTexto = ahora.toLocaleDateString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });

      filas.push({
        label: 'hoy',
        ano: baseAno,
        fechaTexto: hoyTexto,
        edad: baseAno - params.anoNacimiento,
        aporteMes: 0,
        aportacionAnual: 0,
        aporteEsManual: false,
        retiroMensual: 0,
        retiroMes: 0,
        retiroEsManual: false,
        retiroAcumAno: 0,
        patrimonio,
        baseCoste,
        gananciaAno: 0,
        plusvalia: renta0.plus,
        rentaNetaMes: renta0.neta,
        gastoMes: params.gastoMes,
        fire: renta0.neta >= params.gastoMes,
        esFire: false,
        esHoy: true,
        esRetiro: false,
        parcial: false,
        mesesParciales: 0,
        mesFireTexto: ''
      });

      let fireMonth = null;
      let datosFire = null;
      let mesGlobal = 0;
      const metaTarget = Number(metaObjetivo.value) || 0;
      let metaHit = null;

      if (metaTarget > 0 && patrimonio >= metaTarget) {
        metaHit = {
          alcanzado: true,
          mesAnoTexto: formatMesAno(ahora),
          fechaTexto: hoyTexto,
          edad: baseAno - params.anoNacimiento,
          patrimonio,
          yaAlcanzado: true,
          faltanTexto: ''
        };
      }
      const mesesPrimerAno = mesesRestantesPrimerAno(baseMes);
      const overrides = aportacionesOverride.value;
      const retirosO = retirosMensualOverride.value;

      for (let yearIdx = 0; yearIdx < params.horizonte; yearIdx++) {
        const mesesEsteAno = yearIdx === 0 ? mesesPrimerAno : 12;
        const filaAno = baseAno + yearIdx;
        const patrimonioInicioAno = patrimonio;

        // Aporte mensual de este año:
        //  1) si el usuario lo ha fijado manualmente, se respeta
        //  2) si ya hemos alcanzado FIRE, por defecto pasa a 0 (fase retiro)
        //  3) en otro caso, aporte base creciente con IPC anual
        let aporteMesEsteAno;
        const aporteEsManual = overrides[filaAno] !== undefined;
        if (aporteEsManual) {
          aporteMesEsteAno = overrides[filaAno];
        } else if (fireMonth !== null) {
          aporteMesEsteAno = 0;
        } else {
          aporteMesEsteAno = params.aportacionMes * Math.pow(1 + inf, yearIdx);
        }

        // Gasto objetivo del año (constante dentro del año, crece con IPC anual)
        const gastoMesEsteAno = params.gastoMes * Math.pow(1 + inf, yearIdx);
        const fueRetiroAlInicio = fireMonth !== null;
        let aporteAcumAno = 0;
        let retiroAcumAno = 0;

        for (let m = 0; m < mesesEsteAno; m++) {
          mesGlobal++;
          patrimonio = patrimonio * (1 + monthlyR) + aporteMesEsteAno;
          baseCoste += aporteMesEsteAno;
          aporteAcumAno += aporteMesEsteAno;

          const fireCalAno = datosFire ? datosFire.ano : null;
          const retiroMesDefaultNom =
            fireCalAno != null && filaAno >= fireCalAno
              ? params.retiroMensualPostFire * Math.pow(1 + inf, yearIdx)
              : 0;
          const retiroMesPolicy =
            retirosO[filaAno] !== undefined ? retirosO[filaAno] : retiroMesDefaultNom;
          const retiroMesPleno = retiroMesPolicy;

          if (fireMonth !== null && mesGlobal >= fireMonth && retiroMesPleno > 0) {
            const pAfterGrowth = patrimonio;
            const retiroMes = retiroMesPleno;
            if (pAfterGrowth > 0) {
              baseCoste -= retiroMes * (baseCoste / pAfterGrowth);
            }
            patrimonio -= retiroMes;
            if (patrimonio < 0) patrimonio = 0;
            retiroAcumAno += retiroMes;
          }

          const { neta } = calcRenta(patrimonio, baseCoste);
          const fire = neta >= gastoMesEsteAno;

          if (metaTarget > 0 && metaHit === null && patrimonio >= metaTarget) {
            const hitDate = new Date(baseAno, baseMes + mesGlobal, 1);
            metaHit = {
              alcanzado: true,
              mesAnoTexto: formatMesAno(hitDate),
              fechaTexto: hitDate.toLocaleDateString('es-ES', {
                day: '2-digit', month: '2-digit', year: 'numeric'
              }),
              edad: hitDate.getFullYear() - params.anoNacimiento,
              patrimonio,
              yaAlcanzado: false,
              faltanTexto: formatFaltanMeses(mesGlobal)
            };
          }

          if (fire && fireMonth === null) {
            fireMonth = mesGlobal;
            const fireDate = new Date(baseAno, baseMes + mesGlobal, 1);
            const faltanTexto = formatFaltanMeses(mesGlobal);
            const inflFactorMes = Math.pow(1 + inf, yearIdx + (m + 1) / mesesEsteAno);
            datosFire = {
              date: fireDate,
              ano: fireDate.getFullYear(),
              mes: fireDate.getMonth(),
              edadAnos: fireDate.getFullYear() - params.anoNacimiento,
              patrimonio,
              rentaNominalMes: neta,
              rentaRealHoyMes: neta / inflFactorMes,
              gastoNominalMes: gastoMesEsteAno,
              gastoRealHoy: params.gastoMes,
              mesAnoTexto: formatMesAno(fireDate),
              faltanTexto
            };
          }
        }

        const rentaFinAno = calcRenta(patrimonio, baseCoste);
        const esFireAno = fireMonth !== null && datosFire && datosFire.ano === filaAno;
        // Fase retiro: ya estábamos en FIRE al empezar el año (no es el año del FIRE en sí)
        const esRetiro = fueRetiroAlInicio && !esFireAno;

        const fireCalAnoFinal = datosFire ? datosFire.ano : null;
        const retiroMesDefaultFinal =
          fireCalAnoFinal != null && filaAno >= fireCalAnoFinal
            ? params.retiroMensualPostFire * Math.pow(1 + inf, yearIdx)
            : 0;
        const retiroMensualMostrado =
          retirosO[filaAno] !== undefined ? retirosO[filaAno] : retiroMesDefaultFinal;
        const retiroEsManualFila = retirosO[filaAno] !== undefined;

        filas.push({
          label: String(filaAno),
          ano: filaAno,
          fechaTexto: '',
          edad: filaAno - params.anoNacimiento,
          aporteMes: aporteMesEsteAno,
          aportacionAnual: aporteAcumAno,
          aporteEsManual,
          retiroMensual: retiroMensualMostrado,
          retiroMes: retiroMensualMostrado,
          retiroEsManual: retiroEsManualFila,
          retiroAcumAno,
          patrimonio,
          baseCoste,
          gananciaAno: patrimonio - patrimonioInicioAno - aporteAcumAno + retiroAcumAno,
          plusvalia: rentaFinAno.plus,
          rentaNetaMes: rentaFinAno.neta,
          gastoMes: gastoMesEsteAno,
          fire: rentaFinAno.neta >= gastoMesEsteAno,
          esFire: esFireAno,
          esHoy: false,
          esRetiro,
          parcial: yearIdx === 0 && mesesEsteAno < 12,
          mesesParciales: mesesEsteAno,
          mesFireTexto: esFireAno && datosFire ? datosFire.mesAnoTexto : ''
        });
      }

      const ultima = filas[filas.length - 1];
      const metaAlcanceResult = metaTarget > 0 && metaHit
        ? metaHit
        : (metaTarget > 0 ? { alcanzado: false } : { alcanzado: false });

      if (datosFire) {
        return {
          alcanzado: true,
          anoFire: datosFire.ano,
          mesAnoFire: datosFire.mesAnoTexto,
          edadFire: datosFire.edadAnos,
          faltanTexto: datosFire.faltanTexto,
          patrimonioFire: datosFire.patrimonio,
          rentaNominalMes: datosFire.rentaNominalMes,
          rentaRealHoyMes: datosFire.rentaRealHoyMes,
          gastoNominalMes: datosFire.gastoNominalMes,
          diffReal: datosFire.rentaRealHoyMes - params.gastoMes,
          filas,
          metaAlcance: metaAlcanceResult
        };
      }
      const inflTotalFinal = Math.pow(1 + inf, params.horizonte);
      return {
        alcanzado: false,
        anoFire: null,
        mesAnoFire: '',
        edadFire: null,
        faltanTexto: '',
        patrimonioFire: ultima.patrimonio,
        rentaNominalMes: ultima.rentaNetaMes,
        rentaRealHoyMes: ultima.rentaNetaMes / inflTotalFinal,
        gastoNominalMes: ultima.gastoMes,
        diffReal: (ultima.rentaNetaMes / inflTotalFinal) - params.gastoMes,
        filas,
        metaAlcance: metaAlcanceResult
      };
    });

    const metaAlcance = computed(() => resultado.value.metaAlcance || { alcanzado: false });

    const tooltipPatrimonio = 'Valor estimado del patrimonio nominal al 31 de diciembre de cada año (final de año natural). La primera fila "Hoy" muestra el valor actual.';
    const tooltipGananciaCol = computed(() => {
      const base = 'Incluye el efecto de los retiros mensuales tras el FIRE. ';
      return mostrarGananciaMes.value
        ? `${base}Media mensual (ganancia anual ÷ meses de la fila).`
        : `${base}Variación neta del patrimonio menos aportaciones y más retiros del ejercicio.`;
    });
    const tooltipRetiroMensual = 'Retiro mensual nominal desde el mes en que se alcanza el FIRE. 0 € hasta entonces. Se descuenta del patrimonio mes a mes y reduce el coste de adquisición en la misma proporción. Editable por año; el icono de restablecer vuelve al valor del perfil escalado con inflación (como el gasto objetivo).';
    const tooltipAporteAnual = 'Aportación anual nominal. En el año en curso no se cuenta el mes actual (esa cuota ya está en el patrimonio de Hoy). Editable: haz clic en cualquier fila para introducir un importe personalizado. Tras alcanzar FIRE, las aportaciones siguientes se ponen a 0 por defecto (fase retiro).';

    const gananciaMostrada = (fila) => {
      if (fila.esHoy) return 0;
      const meses = fila.mesesParciales > 0 ? fila.mesesParciales : 12;
      if (mostrarGananciaMes.value) return fila.gananciaAno / meses;
      return fila.gananciaAno;
    };

    const chartData = computed(() => {
      const labels = resultado.value.filas.map(f => f.esHoy ? 'Hoy' : String(f.ano));
      return {
        labels,
        datasets: [
          {
            label: 'Patrimonio',
            data: resultado.value.filas.map(f => Math.round(f.patrimonio)),
            borderColor: '#6f42c1',
            backgroundColor: 'rgba(111, 66, 193, 0.12)',
            fill: true,
            tension: 0.3,
            yAxisID: 'y',
            pointRadius: 0,
            borderWidth: 2.5
          },
          {
            label: 'Renta neta/mes',
            data: resultado.value.filas.map(f => Math.round(f.rentaNetaMes)),
            borderColor: '#198754',
            backgroundColor: 'rgba(25, 135, 84, 0)',
            tension: 0.3,
            yAxisID: 'y1',
            pointRadius: 0,
            borderWidth: 2
          },
          {
            label: 'Gasto obj./mes',
            data: resultado.value.filas.map(f => Math.round(f.gastoMes)),
            borderColor: '#fd7e14',
            borderDash: [6, 4],
            backgroundColor: 'rgba(253, 126, 20, 0)',
            tension: 0.3,
            yAxisID: 'y1',
            pointRadius: 0,
            borderWidth: 2
          },
          {
            label: 'Retiro mensual',
            data: resultado.value.filas.map(f => Math.round(f.retiroMensual)),
            borderColor: '#d63384',
            borderDash: [2, 3],
            backgroundColor: 'rgba(214, 51, 132, 0)',
            tension: 0.3,
            yAxisID: 'y1',
            pointRadius: 0,
            borderWidth: 2
          }
        ]
      };
    });

    const chartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              const lbl = ctx.dataset.label;
              return `${lbl}: ${v.toLocaleString('es-ES')} €`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: { color: '#6c757d' },
          grid: { color: 'rgba(0,0,0,0.05)' }
        },
        y: {
          position: 'left',
          ticks: {
            color: '#6c757d',
            callback: (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
          },
          grid: { color: 'rgba(0,0,0,0.05)' },
          title: { display: true, text: 'Patrimonio (€)', color: '#6c757d' }
        },
        y1: {
          position: 'right',
          ticks: { color: '#6c757d' },
          grid: { drawOnChartArea: false },
          title: { display: true, text: '€/mes', color: '#6c757d' }
        }
      }
    }));

    const formatEuros = (v) => {
      if (v == null || isNaN(v)) return '0 €';
      return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(Math.round(v)) + ' €';
    };
    const formatEntero = (v) => {
      if (v == null || isNaN(v)) return '0';
      return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(Math.round(v));
    };
    const formatCompact = (v) => {
      if (v == null || isNaN(v)) return '—';
      const abs = Math.abs(v);
      if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M€`;
      if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}k€`;
      return `${Math.round(v)}€`;
    };

    return {
      subtab,
      metaObjetivo,
      metaAlcance,
      params,
      edadActual,
      baseCosteActual,
      plusvaliaActual,
      resultado,
      chartData,
      chartOptions,
      formatEuros,
      formatEntero,
      formatCompact,
      resetParams,
      onAporteAnualChange,
      restaurarAporte,
      restaurarPatrimonio,
      restaurarAportacionBase,
      restaurarGastoMes,
      slidersBloqueados,
      toggleBloqueo,
      desbloquearTodos,
      bloquearTodos,
      algunDesbloqueado,
      maxPatrimonioSlider,
      maxAportacionSlider,
      maxGastoSlider,
      maxRetiroMensualSlider,
      patrimonioSugerido: computed(() => props.patrimonioInicial),
      aportacionSugeridaMes: computed(() => props.aportacionSugeridaMes),
      gastoMesSugerido: computed(() => props.gastoMesSugerido),
      tooltipPatrimonio,
      tooltipGananciaCol,
      tooltipRetiroMensual,
      tooltipAporteAnual,
      mostrarGananciaMes,
      gananciaMostrada,
      onRetiroMensualChange,
      restaurarRetiro
    };
  }
};
</script>

<style scoped>
.calc-fire { color: #212529; }

.calc-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff6b35, #ffa42a);
  color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1rem;
  box-shadow: 0 4px 14px rgba(255, 107, 53, 0.3);
  flex-shrink: 0;
}

.kpi-label {
  color: #6c757d;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}
.kpi-value {
  color: #212529;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
}
.kpi-sub {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 0.15rem;
}

.calc-pills {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 4px;
  border: 1px solid #dee2e6;
}
.calc-pills .nav-link {
  color: #495057;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.88rem;
  padding: 0.45rem 0.6rem;
}
.calc-pills .nav-link.active {
  background: #6f42c1;
  color: #fff;
  box-shadow: 0 2px 6px rgba(111, 66, 193, 0.3);
}
.calc-pills .nav-link:not(.active):hover {
  background: rgba(111, 66, 193, 0.08);
  color: #6f42c1;
}

.param-block {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.6rem 0.85rem 0.85rem;
  margin-bottom: 0.6rem;
}
.param-row {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px;
  margin-bottom: 0.4rem;
}
.param-label {
  display: inline-flex; align-items: center; gap: 8px;
  color: #212529;
  font-weight: 500;
  font-size: 0.85rem;
}
.param-label i {
  color: #6c757d;
  font-size: 0.85rem;
  width: 14px;
  text-align: center;
}
.param-value {
  color: #212529;
  font-weight: 700;
  font-size: 0.88rem;
  text-align: right;
  white-space: nowrap;
}
.param-value small { font-weight: 400; color: #6c757d; font-size: 0.78rem; }

.param-group {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.6rem;
}
.param-group-title {
  color: #6c757d;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.mini-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.4rem 0.7rem;
}
.mini-label { color: #6c757d; font-size: 0.72rem; }
.mini-value { color: #212529; font-weight: 700; font-size: 0.92rem; }

.modelo-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
}
.modelo-title {
  color: #6c757d;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.4rem;
  font-weight: 600;
}
.modelo-row {
  display: flex; justify-content: space-between;
  font-size: 0.85rem;
  padding: 2px 0;
  color: #212529;
}

/* Slider personalizado robusto */
.fire-slider {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  background: #dee2e6;
  border-radius: 3px;
  outline: none;
  margin: 0.5rem 0 0;
  padding: 0;
  cursor: pointer;
}
.fire-slider:focus { outline: none; }

.fire-slider::-webkit-slider-runnable-track {
  height: 6px;
  background: #dee2e6;
  border-radius: 3px;
  border: none;
}
.fire-slider::-moz-range-track {
  height: 6px;
  background: #dee2e6;
  border-radius: 3px;
  border: none;
}

.fire-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #6f42c1;
  box-shadow: 0 2px 4px rgba(111, 66, 193, 0.35);
  cursor: pointer;
  margin-top: -5px;
  transition: transform 0.1s;
}
.fire-slider::-moz-range-thumb {
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #fff;
  border: 3px solid #6f42c1;
  box-shadow: 0 2px 4px rgba(111, 66, 193, 0.35);
  cursor: pointer;
  transition: transform 0.1s;
}
.fire-slider:hover::-webkit-slider-thumb { transform: scale(1.1); }
.fire-slider:hover::-moz-range-thumb { transform: scale(1.1); }
.fire-slider:active::-webkit-slider-thumb { transform: scale(1.15); }
.fire-slider:active::-moz-range-thumb { transform: scale(1.15); }

/* Slider bloqueado */
.fire-slider:disabled {
  cursor: not-allowed;
  opacity: 0.55;
  pointer-events: none;
}
.fire-slider:disabled::-webkit-slider-thumb {
  background: #f1f3f5;
  border-color: #adb5bd;
  box-shadow: none;
  cursor: not-allowed;
}
.fire-slider:disabled::-moz-range-thumb {
  background: #f1f3f5;
  border-color: #adb5bd;
  box-shadow: none;
  cursor: not-allowed;
}

/* Botón candado por slider */
.btn-lock {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  color: #6c757d;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}
.btn-lock i { font-size: 0.78rem; line-height: 1; }
.btn-lock:hover {
  background: #e9ecef;
  color: #495057;
}
.btn-lock:active { transform: scale(0.92); }
.btn-lock.is-open {
  background: rgba(111, 66, 193, 0.12);
  border-color: rgba(111, 66, 193, 0.35);
  color: #6f42c1;
}
.btn-lock.is-open:hover {
  background: rgba(111, 66, 193, 0.2);
}

/* Banner de aviso */
.lock-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff8e6;
  border: 1px solid #ffe69c;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
}
.lock-banner-text {
  color: #664d03;
  font-size: 0.82rem;
  line-height: 1.35;
  flex: 1 1 240px;
}
.lock-banner-btn {
  flex-shrink: 0;
  font-size: 0.78rem;
  padding: 0.25rem 0.65rem;
}

.calc-table {
  font-size: 0.85rem;
  margin-bottom: 0;
}
.calc-table thead th {
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #495057;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 0.55rem 0.6rem;
  white-space: nowrap;
  position: sticky; top: 0;
  background: #f8f9fa;
}
.calc-table tbody td {
  padding: 0.45rem 0.6rem;
  vertical-align: middle;
}
.calc-table tr.fila-fire td {
  background: rgba(25, 135, 84, 0.08) !important;
  border-top: 2px solid #198754;
  border-bottom: 2px solid #198754;
}
.calc-table tr.fila-fire td:first-child {
  border-left: 3px solid #198754;
}
.calc-table tr.fila-hoy td {
  background: rgba(13, 110, 253, 0.05) !important;
  border-bottom: 1px solid #b6d4fe;
}
.calc-table tr.fila-hoy td:first-child {
  border-left: 3px solid #0d6efd;
  font-weight: 700;
}
.calc-table tr.fila-retiro td {
  background: rgba(111, 66, 193, 0.04) !important;
}

/* Columnas secundarias (Plusvalía / Gasto obj.) — color tenue pero legible */
.calc-table .col-secundaria {
  color: #495057;
}
.calc-table tr.fila-retiro .col-secundaria,
.calc-table tr.fila-fire .col-secundaria {
  color: #343a40;
}

/* Ganancia generada por el patrimonio cada año */
.calc-table .col-ganancia-ano {
  white-space: nowrap;
}
.calc-table tr.fila-hoy .col-ganancia-ano {
  color: #6c757d;
}

/* Input editable de aportación anual */
.aporte-input {
  width: 100px;
  text-align: right;
  font-size: 0.82rem;
  padding: 0.15rem 0.4rem;
  height: auto;
  line-height: 1.2;
  border: 1px dashed #ced4da;
  background: transparent;
  font-weight: 500;
}
.aporte-input:hover {
  border-color: #6f42c1;
  background: #fff;
}
.aporte-input:focus {
  border-style: solid;
  border-color: #6f42c1;
  background: #fff;
  box-shadow: 0 0 0 0.15rem rgba(111, 66, 193, 0.18);
  outline: none;
}
.aporte-input.aporte-manual {
  border-style: solid;
  border-color: #6f42c1;
  background: rgba(111, 66, 193, 0.06);
  color: #6f42c1;
  font-weight: 600;
}
.btn-reset-aporte {
  width: 22px; height: 22px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 4px;
  color: #6c757d;
  background: transparent;
  border: none;
  font-size: 0.78rem;
  flex-shrink: 0;
}
.btn-reset-aporte:hover {
  background: rgba(111, 66, 193, 0.12);
  color: #6f42c1;
}

/* Hide native number input spinners (más limpio) */
.aporte-input::-webkit-outer-spin-button,
.aporte-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.aporte-input { -moz-appearance: textfield; }

/* Badges purple para fase retiro */
.bg-purple-subtle { background-color: rgba(111, 66, 193, 0.12) !important; }
.text-purple { color: #6f42c1 !important; }

.legend-text {
  color: #6c757d;
  font-size: 0.8rem;
  text-align: center;
}
.legend-text .dot {
  display: inline-block;
  width: 9px; height: 9px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}

.meta-input-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 420px;
}
.meta-label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #212529;
}
.meta-input-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.meta-input {
  text-align: right;
  font-weight: 700;
  font-size: 1.25rem;
  border-color: #dee2e6;
}
.meta-input:focus {
  border-color: #6f42c1;
  box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.2);
}
.meta-suffix {
  font-weight: 600;
  color: #6c757d;
  font-size: 1.1rem;
}
.meta-result {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.1rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
}
.meta-result-icon {
  font-size: 2rem;
  line-height: 1;
  flex-shrink: 0;
}
.meta-result-title {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.2rem;
}
.meta-result-date {
  font-size: 1.65rem;
  font-weight: 700;
  color: #6f42c1;
  line-height: 1.2;
}
.meta-result-sub {
  font-size: 0.88rem;
}

@media (max-width: 575.98px) {
  .kpi-value { font-size: 1.05rem; }
  .calc-pills .nav-link { font-size: 0.78rem; padding: 0.4rem 0.4rem; }
  .calc-table { font-size: 0.78rem; }
  .calc-table thead th, .calc-table tbody td { padding: 0.4rem 0.5rem; }
  .param-label { font-size: 0.8rem; }
  .param-value { font-size: 0.82rem; }
  .param-value small { display: block; font-size: 0.72rem; }
}
</style>
